import { FileUploadAction, setInProgress, setError, setNewFile } from './store/Actions'
import { Http } from 'lib'
import IFileUploadResponse from 'types/file-upload/IFileUploadResponse'
import OnSuccessDelegate from 'types/OnSuccessDelegate'
import OnErrorDelegate from 'types/OnErrorDelegate'
import IFileUploadParams, { FileType, FileExtension } from 'types/file-upload/IFileUploadParams'
import IAvatarUploadPayload from 'types/file-upload/IAvatarUploadPayload'
import apiUrlsAddresses from '../../config/constants/api-urls-addresses'

export default class FileUploadService {
  static isValidType(file: Blob, allowedTypes: FileType[], allowedExtensions: FileExtension[]): boolean {
    const [type, extension] = file.type.split('/') as [FileType, FileExtension]
    if (allowedTypes.includes('all')) return true

    return allowedTypes.includes(type) && allowedExtensions.includes(extension)
  }

  static isValidSize(file: Blob, size: number): boolean {
    return file.size / 1024 <= size
  }

  static readDataFromUrl(url: string): Promise<string> {
    return fetch(url)
      .then(response => response.blob())
      .then(
        blob =>
          new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
      )
  }

  static readDataUrl(file: Blob): Promise<string> {
    const temporaryFileReader = new FileReader()

    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort()
        reject(new DOMException('Problem parsing input file.'))
      }

      temporaryFileReader.readAsDataURL(file)
      temporaryFileReader.onloadend = () => {
        resolve(temporaryFileReader.result as string)
      }
    })
  }

  static onAvatarRemoved(
    dispatch: React.Dispatch<FileUploadAction>,
    onSuccess: OnSuccessDelegate<IFileUploadResponse>,
    onError: OnErrorDelegate
  ) {
    return async () => {
      dispatch(setError(null))
      dispatch(setInProgress(true))

      const [response, , requestError] = await FileUploadService.sendAvatar(null)

      if (requestError) {
        onError(requestError as string)
      } else {
        onSuccess(response)
        dispatch(setNewFile(''))
      }
      dispatch(setInProgress(false))
    }
  }

  static async sendAvatar(file: File): Promise<[IFileUploadResponse | null, number, string | null]> {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const formData = new FormData()
    formData.append('image', file)
    return Http.post<IAvatarUploadPayload, IFileUploadResponse>(apiUrlsAddresses.me.avatar(), formData, null, headers)
  }

  static onFileSelected(
    dispatch: React.Dispatch<FileUploadAction>,
    onSuccess: OnSuccessDelegate<IFileUploadResponse>,
    onError: OnErrorDelegate,
    params?: IFileUploadParams
  ) {
    return async (file: File) => {
      if (params) {
        if (
          params.allowedTypes &&
          !FileUploadService.isValidType(file, params.allowedTypes, params.allowedExtensions)
        ) {
          onError('This file type is now allowed')
          return
        }

        if (params.maxSize && !FileUploadService.isValidSize(file, params.maxSize)) {
          onError(`This file is too big`)
          return
        }
      }
      dispatch(setInProgress(true))

      dispatch(setError(null))

      const base64file = await FileUploadService.readDataUrl(file)
      const [response, , requestError] = await FileUploadService.sendAvatar(file)

      if (requestError) {
        onError(requestError as string)
      } else {
        onSuccess(response)
        dispatch(setNewFile(base64file))
      }

      dispatch(setInProgress(false))
    }
  }
}
